import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Copyable,
  DateUI,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  PrimaryFullScreenSpinner,
  TransferCard,
  toast,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/calastone-orders_/$calastoneOrderId",
)({
  head: () => opsHead("Calastone Order"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { calastoneOrderId } = Route.useParams();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminCalastoneOrderDetail($id: ID!) {
        node(id: $id) {
          id
          ... on CalastoneOrder {
            id
            shortRef
            status
            reason
            calastoneId
            debugData
            createdAt
            updatedAt
            transfer {
              id
              paymentReference
              ...TransferCardFields
              organisation {
                id
                ...OrganisationCardFields
              }
            }
          }
        }
      }
    `),
    { variables: { id: calastoneOrderId } },
  );

  const [syncCalastoneOrder, { loading: syncing }] = useMutation(
    graphql(`
      mutation AdminSyncCalastoneOrder($id: ID!) {
        syncCalastoneOrder(input: {id: $id}) {
          success
        }
      }
    `),
    {
      onCompleted: () =>
        toast({
          kind: "success",
          autoHide: true,
          title: "Order sync successfully triggered",
        }),
      onError: (err) =>
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to queue sync",
          description: err.graphQLErrors?.[0]?.message ?? err.message,
        }),
    },
  );

  const [restartCalastoneOrder, { loading: restarting }] = useMutation(
    graphql(`
      mutation AdminRestartCalastoneOrder($id: ID!) {
        restartCalastoneOrder(input: {id: $id}) {
          success
        }
      }
    `),
    {
      onCompleted: () =>
        toast({
          kind: "success",
          autoHide: true,
          title: "Order restart successfully triggered",
        }),
      onError: (err) =>
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to restart order",
          description: err.graphQLErrors?.[0]?.message ?? err.message,
        }),
    },
  );

  if (!data.node || data.node.__typename !== "CalastoneOrder") {
    throw new NotFoundError();
  }

  const order = data.node;

  useOpsTitle(`Calastone Order #${order.shortRef}`);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`Calastone Order #${order.shortRef}`}
        subtitle={<div className="font-mono">{order.id}</div>}
      />
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Transfer</CardTitle>
          </CardHeader>
          <CardContent>
            <Link
              to="/transfers/$transferId"
              params={{ transferId: order.transfer.id }}
            >
              <div className="transition-opacity hover:opacity-75">
                <TransferCard
                  transfer={order.transfer}
                  variant="TRANSFER_OUT"
                  subText={["DATE", "PAYMENT_REFERENCE"]}
                />
              </div>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Details</CardTitle>
            <div className="flex gap-2">
              {order.status === "FAILED_TO_CREATE" && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={restarting}
                  disabled={restarting}
                  onClick={() =>
                    restartCalastoneOrder({ variables: { id: order.id } })
                  }
                >
                  Retry creation
                </Button>
              )}
              {order.status !== "PENDING" && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={syncing}
                  disabled={syncing}
                  onClick={() =>
                    syncCalastoneOrder({ variables: { id: order.id } })
                  }
                >
                  Refresh
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <DetailRow
              label="Status"
              value={
                <span className="text-sm">{prettyPrintEnum(order.status)}</span>
              }
            />
            <DetailRow
              label="Organisation"
              value={
                order.transfer.organisation ? (
                  <OrganisationCard
                    organisation={order.transfer.organisation}
                    linkTo={`/organisations/${order.transfer.organisation.id}`}
                  />
                ) : null
              }
            />
            <DetailRow
              label="Short ref"
              value={<span className="font-mono">#{order.shortRef}</span>}
            />
            <DetailRow
              label="ID"
              value={
                <Copyable tooltipSide="right" className="font-mono text-sm">
                  {order.id}
                </Copyable>
              }
            />
            <DetailRow
              label="Calastone ID"
              value={
                order.calastoneId ? (
                  <Copyable tooltipSide="right" className="font-mono text-sm">
                    {order.calastoneId}
                  </Copyable>
                ) : (
                  <span className="text-tertiary">—</span>
                )
              }
            />
            <DetailRow
              label="Reason"
              value={
                order.reason ? (
                  order.reason
                ) : (
                  <span className="text-tertiary">—</span>
                )
              }
            />
            <DetailRow
              label="Created at"
              value={<DateUI date={order.createdAt} variant="DATETIME_TZ" />}
            />
            <DetailRow
              label="Updated at"
              value={<DateUI date={order.updatedAt} variant="DATETIME_TZ" />}
            />
            {order.debugData != null && (
              <div className="pt-4">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="secondary" size="sm">
                      Preview raw data
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden sm:max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Raw data</DialogTitle>
                    </DialogHeader>
                    <pre className="flex-1 overflow-auto whitespace-pre-wrap break-all rounded-md bg-secondary p-4 font-mono text-xs">
                      {(() => {
                        try {
                          return JSON.stringify(
                            JSON.parse(order.debugData!),
                            null,
                            2,
                          );
                        } catch {
                          return order.debugData;
                        }
                      })()}
                    </pre>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminPageContainer>
  );
}
